/**
 * Single source of truth for per-line-type appearance.
 *
 * A line's `lineType` attribute (speech / embodied / gloss / translation /
 * default) is purely semantic — it records *what kind* of line it is. This
 * module maps each type to *how it looks*: weight, italic, and colour.
 *
 * The same style object drives BOTH consumers, so they can never drift:
 *   - On screen, via CSS custom properties applied in App.jsx
 *     (see `lineStyleToCSSVars`), consumed by the `p.<type>-line` rules.
 *   - In Word / rich-HTML export, via `transcriptStyles.js`
 *     (see `lineStyleToSerializerStyle`).
 *
 * Style shape: { bold: boolean, italic: boolean, color: string }
 *   `color` is either the sentinel 'default' (follow the editor's foreground
 *   on screen / omit colour in export, i.e. plain black on white) or one of
 *   the grey hexes from LINE_STYLE_COLORS.
 *
 * The "default" line type (plain body text) is intentionally not customizable
 * — it is the baseline every other type is measured against.
 */

// Colour swatches offered in the Line Styles dialog. The first ('default')
// follows the editor foreground so it stays readable in dark mode and exports
// as plain black; the rest are literal greys applied in every theme.
export const LINE_STYLE_COLORS = [
  { value: 'default', label: 'Black', swatch: '#000000' },
  { value: '#595959', label: 'Dark grey', swatch: '#595959' },
  { value: '#888888', label: 'Grey', swatch: '#888888' },
  { value: '#a9a9a9', label: 'Light grey', swatch: '#a9a9a9' },
  { value: '#c0c0c0', label: 'Pale grey', swatch: '#c0c0c0' },
];

// The customizable line types, in display order. Excludes 'default'.
export const STYLEABLE_LINE_TYPES = ['speech', 'embodied', 'gloss', 'translation'];

// Shipped defaults — reproduce the app's original appearance exactly:
// speech bold, embodied mid-grey, translation italic, gloss plain.
export const DEFAULT_LINE_STYLES = {
  speech: { bold: true, italic: false, color: 'default' },
  embodied: { bold: false, italic: false, color: '#888888' },
  gloss: { bold: false, italic: false, color: 'default' },
  translation: { bold: false, italic: true, color: 'default' },
};

/**
 * Convert the full lineStyles object to a map of CSS custom properties for
 * on-screen rendering, e.g. { '--ls-speech-weight': 'bold', ... }.
 * A 'default' colour becomes `inherit` so the line follows the editor
 * foreground (theme-aware).
 */
export function lineStyleToCSSVars(lineStyles) {
  const vars = {};
  for (const type of STYLEABLE_LINE_TYPES) {
    const s = lineStyles[type] ?? DEFAULT_LINE_STYLES[type];
    vars[`--ls-${type}-weight`] = s.bold ? 'bold' : 'normal';
    vars[`--ls-${type}-style`] = s.italic ? 'italic' : 'normal';
    vars[`--ls-${type}-color`] = s.color === 'default' ? 'inherit' : s.color;
  }
  return vars;
}

/**
 * Convert a single line-type style to the camelCase style object the
 * serializers understand. A 'default' colour is omitted entirely so export
 * falls back to plain black on the page.
 */
export function lineStyleToSerializerStyle(style) {
  const out = {};
  if (style.bold) out.fontWeight = 'bold';
  if (style.italic) out.fontStyle = 'italic';
  if (style.color && style.color !== 'default') out.color = style.color;
  return out;
}

/**
 * Merge a (possibly partial / persisted) override object over the shipped
 * defaults, validating shape so a corrupt localStorage value can't break
 * rendering. Unknown keys are ignored; missing fields fall back to defaults.
 */
export function mergeLineStyles(overrides) {
  const merged = {};
  for (const type of STYLEABLE_LINE_TYPES) {
    const base = DEFAULT_LINE_STYLES[type];
    const o = overrides && typeof overrides === 'object' ? overrides[type] : null;
    if (o && typeof o === 'object') {
      merged[type] = {
        bold: typeof o.bold === 'boolean' ? o.bold : base.bold,
        italic: typeof o.italic === 'boolean' ? o.italic : base.italic,
        color: typeof o.color === 'string' ? o.color : base.color,
      };
    } else {
      merged[type] = { ...base };
    }
  }
  return merged;
}
